import { T, type Trilingual } from './i18n.ts';
import type { CardBtn } from '@jyfk/settings/cards';

/**
 * 卡片上那些**不是设置项**的东西：定制块、按钮、说明 —— 网页版哪些做了、哪些没做。
 *
 * ════════════════════════════════════════════════════════
 * 为什么要逐个登记
 * ════════════════════════════════════════════════════════
 * 原型的卡片上有 55 个定制块（统计图、列表、二维码……）和 40 多排按钮。
 * 以前网页版生成清单时把它们全扔了，于是一张卡片上的按钮和统计图凭空消失，
 * 剩下的看起来是一张完整的卡片 —— 用户不知道少了什么，我们自己也会忘。
 *
 * 现在每一个都要在这里有个交代，界面按它来画：
 *   built      网页版做了，就画在这张卡片里
 *   replaced   网页版用别的方式做了（这一页上面的真数据、整页的保存条、边改边看的预览），不再重复画
 *   elsewhere  在别的页面上 —— 画一行指路
 *   soon       还没做 —— 列进「还没做：……」那一行，**不许静默消失**
 *   desktop    桌面版才有 —— 列进「桌面版才有：……」
 *
 * 定制块有测试盯着：清单里出现一个这里没登记的名字，测试会红。
 * 按钮不逐个登记：没登记的一律当「还没做」列出来（宁可少说做了，不可多说）。
 */

export type BlockStatus = 'built' | 'replaced' | 'elsewhere' | 'soon' | 'desktop';

export interface BlockInfo {
  status: BlockStatus;
  /** 「还没做：……」里怎么称呼它 */
  label: Trilingual;
  /** 在哪 / 为什么（elsewhere 必填） */
  note?: Trilingual;
}

const replaced = (zh: string): BlockInfo => ({ status: 'replaced', label: T(zh, zh, zh) });

export const CUSTOM_BLOCKS: Readonly<Record<string, BlockInfo>> = {
  /* ── 交易纪律 ── */
  expResult: {
    status: 'elsewhere',
    label: T('期望值统计结果', '期望值統計結果', 'Expectancy results'),
    note: T('统计结果在总览页的「期望值」卡片里。', '統計結果在總覽頁的「期望值」卡片裏。', 'The results are on the Overview page, in the Expectancy card.')
  },
  /* 2026-10-29 做了：pages/MoreCards.tsx 的 FreqResultCard，接在这张卡下面（GET /api/overview 的 today） */
  freqResult: { status: 'built', label: T('今天和这一小时已经做了几笔', '今天和這一小時已經做了幾筆', 'Trades so far today and this hour') },
  /* 2026-10-28 做了：整张卡由 pages/Fomo.tsx 的两个组件代替（统计 / 补录 + 记录列表） */
  fomoStats: { status: 'built', label: T('FOMO 抵抗统计', 'FOMO 抵抗統計', 'FOMO resistance stats') },
  fomoList: { status: 'built', label: T('抵抗记录列表', '抵抗記錄列表', 'Resistance log') },

  /* ── 钱包监控 ── */
  /* 限流器的实时状态在扩展里（令牌桶和冷却只在用户自己的浏览器里），扩展图标的弹窗上看得到冷却和出错 */
  limiterState: { status: 'elsewhere', label: T('限流器的实时状态', '限流器的即時狀態', 'Live rate-limiter state'),
    note: T('在扩展图标的弹窗里看：冷却中、还要多久、哪个钱包出错都写着。限流器跑在你自己的浏览器里，服务器看不到它。',
      '在擴充功能圖示的彈窗裏看：冷卻中、還要多久、哪個錢包出錯都寫著。限流器跑在你自己的瀏覽器裏，伺服器看不到它。',
      'See it in the extension popup: cooldown, time left and failing wallets. The limiter runs in your browser, so the server cannot see it.') },
  keyList: {
    status: 'elsewhere',
    label: T('Key 列表', 'Key 列表', 'Key list'),
    note: T('Key 在浏览器扩展的设置页里。', 'Key 在瀏覽器擴充功能的設定頁裏。', 'Keys live in the extension settings.')
  },
  /* 额度消耗预估：扩展弹窗按你现在的钱包数、间隔和限流设置算，撑不住时说清是哪个瓶颈 */
  usageBox: { status: 'elsewhere', label: T('额度消耗预估', '額度消耗預估', 'Quota forecast'),
    note: T('在扩展图标的弹窗里看：钱包偏多、额度不够时会写「只够盯几个」，以及是额度还是排班不够。',
      '在擴充功能圖示的彈窗裏看：錢包偏多、額度不夠時會寫「只夠盯幾個」，以及是額度還是排班不夠。',
      'See it in the extension popup: it says how many wallets your quota covers and which limit is the bottleneck.') },
  /* 服务器不替用户调 GMGN（老板 2026-10-29 定的），出口检测用不上 */
  egressBox: { status: 'desktop', label: T('出口 IP 与连通性检测', '出口 IP 與連通性檢測', 'Egress IP and connectivity check') },
  usageWallets: replaced('钱包用量'),
  walletList: replaced('钱包列表'),

  /* ── 价格监控（列表、额度、添加表单都在这一页上面） ── */
  twQuota: replaced('额度用量'),
  twProbe: replaced('先查一下这个币'),
  twList: replaced('监控中的代币'),
  twChDesc: replaced('通道说明'),
  /* 2026-10-29 做了：pages/MoreCards.tsx 的 PriceLogCard（GET /api/inbox?group=price） */
  twLog: { status: 'built', label: T('发过的价格通知记录', '發過的價格通知記錄', 'Price alert history') },

  /* ── 事项提醒 ── */
  usageReminders: replaced('事项用量'),
  reminderList: replaced('事项列表'),
  /* 2026-10-29：原型这块只画不存，改成了真设置项 rm_phaseCh（多选，见 design-prototype/schema.js），这里留着只为原型旧数据 */
  notifyMatrix: replaced('通道 × 时机矩阵'),
  sentinelBox: { status: 'desktop', label: T('哨兵服务器状态', '哨兵伺服器狀態', 'Sentinel server status') },
  /* 折叠组里的定制块：Bark 的提前提醒分级规则 */
  /* 2026-10-29：折叠组里放了真设置项 bk_leadRules（components/LeadRules.tsx 画），这个名字只为原型旧数据留着 */
  barkLeadRules: replaced('按还剩多久匹配的分级规则'),

  /* ── 备忘录（用量、标签栏、列表都在这一页上面了，见 pages/MemoPage.tsx） ── */
  usageMemos: replaced('备忘用量'),
  memoTags: replaced('按标签筛选'),
  memoList: replaced('备忘列表'),

  /* ── 通用设置 ── */
  /* 小伙伴列表和「添加小伙伴」那个表单做完了（见 pages/Buddies.tsx）：
     整张卡由 BuddyManageCard 代替，所以这里是 built */
  buddyList: { status: 'built', label: T('一键喊话小伙伴', '一鍵喊話小夥伴', 'Buddy alert') },
  /* 2026-10-28 做了：开关和当前状态由 pages/Dnd.tsx 的 DndCard 画在这张设置卡前面 */
  dndState: { status: 'built', label: T('免打扰的当前状态', '免打擾的目前狀態', 'Current do-not-disturb state') },
  /* 2026-10-29 做了：pages/HotkeyCard.tsx 按他现在的设置列出网站里有效的快捷键（G 跳页、收件箱五个键、Esc） */
  hotkeyList: { status: 'built', label: T('快捷键列表', '快捷鍵列表', 'Shortcut list') },
  /* 网页版用浏览器自带的声音念（lib/voice.ts），没有服务器音色、也就没有要缓存的东西 */
  voiceCache: { status: 'desktop', label: T('语音缓存', '語音快取', 'Voice cache') },
  /* 2026-10-29 做了：整张「数据与维护」卡由 pages/DataCard.tsx 代替（存储用量、导出、导入、全部恢复默认） */
  storageInfo: { status: 'built', label: T('存储用量', '儲存用量', 'Storage usage') },

  /* ── 系统与诊断 ── */
  /* 2026-10-29 做了：整张卡由 pages/SystemCards.tsx 的 CheckCard 代替（GET /api/system/check） */
  selfTest: { status: 'built', label: T('自检结果', '自檢結果', 'Self-test results') },
  /* 2026-10-29 做了：整张卡由 pages/SystemCards.tsx 的 ProofCard 代替（五路心跳 + 扩展在线） */
  proofTable: { status: 'built', label: T('「它在正常保护我」的判断表', '「它在正常保護我」的判斷表', '"Is it protecting me" checklist') },
  diagResult: { status: 'built', label: T('全面检测结果', '全面檢測結果', 'Diagnostics results') },
  serverHealth: { status: 'built', label: T('服务器健康状态', '伺服器健康狀態', 'Server health') },
  troubleTable: { status: 'built', label: T('手动排查清单', '手動排查清單', 'Troubleshooting table') },
  journal: { status: 'built', label: T('纪律执行记录', '紀律執行記錄', 'Discipline log') },

  /* ── 外观与语言 ── */
  /* 预览：网页版改了立刻在整个页面上预览（见 SettingsPage 的 previewLook），不另画一块 */
  iconPreview: replaced('图标预览'),
  feelPreview: replaced('手感预览'),
  lookPreview: replaced('外观预览'),
  langPreview: replaced('语言预览'),
  /* 2026-10-29 做了：整张卡由 pages/UserIconsCard.tsx 代替（图存在这台浏览器里，额度 icons 在 lib/user-icons.ts 执行） */
  userIcons: { status: 'built', label: T('自定义图标的上传与替换', '自訂圖示的上傳與替換', 'Custom icon upload') },
  usageIcons: replaced('自定义图标用量'),
  layoutPanel: { status: 'built', label: T('布局自定义', '佈局自訂', 'Layout') },
  navEditor: { status: 'built', label: T('导航', '導航', 'Navigation') },
  loginMethods: { status: 'built', label: T('登录方式', '登入方式', 'Sign-in methods') },
  /* 2026-10-29 做了：和「登录方式」画在一起（pages/MoreCards.tsx 的 DevicesCard，GET /api/me/sessions） */
  deviceList: replaced('登录过的设备'),

  /* ── 订阅（这一页上面是真的套餐、下单、二维码、订单） ── */
  planStatus: replaced('当前状态'),
  planCompare: replaced('套餐对比'),
  planPicker: replaced('选套餐'),
  payQr: replaced('付款二维码'),
  orderList: replaced('历史订单'),

  /* ── 我的邀请：整页还没做（网页从来没有把邀请关系绑上去） ── */
  /* 2026-10-29 做了：整页由 pages/ReferralPage.tsx 画（GET /api/referral） */
  refLink: replaced('我的邀请链接'),
  refStats: replaced('邀请战绩'),
  refLadder: replaced('返佣比例阶梯'),
  refCashout: replaced('返佣提现或折时长'),
  refList: replaced('返佣明细'),
  refRules: replaced('规则说明')
};

export function customBlock(name: string): BlockInfo {
  /* 没登记的一律当「还没做」—— 测试会盯着不许出现这种情况，这里只是兜底 */
  return CUSTOM_BLOCKS[name] ?? { status: 'soon', label: T(name, name, name) };
}

/* ══════════════════════ 按钮 ══════════════════════ */

/**
 * 按钮的状态。键是「动作名」（原型里有的话）或「页面|按钮文字」。
 * 没登记的一律「还没做」；文字以「保存」开头的由整页的保存条代替。
 */
const BUTTONS: Readonly<Record<string, BlockStatus>> = {
  /* 界面折叠：做了（见 SettingsPage 的 useFolds） */
  'act:foldAll': 'built',
  'act:foldNone': 'built',
  'act:foldReset': 'built',
  /* 外观页的「恢复默认外观」：页头那个「恢复默认」就是它 */
  'act:resetLook': 'replaced',
  /* GMGN 的 Key 在扩展里填 */
  'act:wizard': 'elsewhere',
  'wallet|显示 / 隐藏完整内容': 'elsewhere',
  /* 加小伙伴在上面那张「喊话小伙伴名单」卡里（做完了，见 pages/Buddies.tsx） */
  'general|添加小伙伴': 'replaced',
  /* 免打扰的时长按钮和「立刻关闭」在上面那张 DndCard 里（pages/Dnd.tsx）。
     「现在补发 / 清空暂存」不做：结束时自动汇总补发，被压下的每一条都留在收件箱里，不存在要清的暂存 */
  'general|30 分钟': 'replaced',
  'general|1 小时': 'replaced',
  'general|2 小时': 'replaced',
  'general|4 小时': 'replaced',
  'general|立刻关闭': 'replaced',
  'general|现在补发': 'replaced',
  'general|清空暂存': 'replaced',
  /* 自己的哨兵服务器是桌面版的 */
  'reminders|测试连接': 'desktop',
  'reminders|部署 / 更新哨兵': 'desktop',
  'reminders|查看状态': 'desktop',
  /* 通知的测试、试听、一键绑定（做了：动作见 lib/notify-actions.ts，接口是 /api/notify/*）。
     「发送测试消息」在 Telegram 卡和飞书卡上是同一句话，两张都接上了。 */
  'reminders|一键绑定 Telegram': 'built',
  'reminders|发送测试消息': 'built',
  'reminders|发送测试推送': 'built',
  'reminders|试听到点推送': 'built',
  'reminders|试听催办推送': 'built',
  /* 系统页（2026-10-29）：自检、检测的按钮画在卡片里；刷新、导出画在执行记录那张卡的标题栏 */
  'act:selftest': 'replaced',
  'act:diag': 'replaced',
  'system|刷新': 'replaced',
  'system|导出记录': 'replaced',
  /* 「测试提醒效果」：按那一种提醒的样子发一条测试到事项提醒那一套通道（见 lib/notify-actions.ts）。
     下单前确认（纪律 2 删了）、熔断锁屏（网页没有全屏遮罩）是桌面版的 */
  'act:testPatience': 'built',
  'act:testSitting': 'built',
  'act:testProfit': 'built',
  'act:testCreed': 'built',
  /* 快捷键那张卡：改键在「操作习惯」里，整页底部的保存 / 页头的「恢复默认」代替这两个 */
  'general|全部恢复默认': 'replaced',
  /* 「数据与维护」卡的按钮都画在 DataCard 里；「删除账号」还没做，那张卡上如实写着 */
  'general|导出全部数据（JSON）': 'replaced',
  'general|导出交易记录（CSV）': 'replaced',
  'general|导出备忘录': 'replaced',
  'general|导入数据': 'replaced',
  'general|恢复默认设置': 'replaced',
  'general|删除账号和全部数据': 'replaced',
  /* 声音与语音：试听做了（浏览器自带的声音）；服务器 Edge 音色那一套（逐个试听、拉音色库、预热、缓存）是桌面版的 */
  'act:speak': 'built',
  /* 「试听提示音」（2026-10-30） */
  'act:tone': 'built',
  /* 「打开链热度设置」：跳到链热度那一页（2026-10-30） */
  'act:chainCfg': 'built',
  'general|逐个试听所有中文音色': 'desktop',
  'general|拉取完整音色库': 'desktop',
  'general|立刻预热全部提醒语': 'desktop',
  'general|清空语音缓存': 'desktop',
  /* 网页版只用一个 Key（老板 2026-10-29 定的），没有「全部 Key」可测 */
  'wallet|测试全部 Key': 'desktop',
  'act:testChecklist': 'desktop',
  'act:testLock': 'desktop'
};

export function buttonStatus(page: string, b: CardBtn): BlockStatus {
  const zh = b.label?.zh ?? '';
  if (b.act && BUTTONS['act:' + b.act]) return BUTTONS['act:' + b.act]!;
  const byLabel = BUTTONS[page + '|' + zh];
  if (byLabel) return byLabel;
  if (zh.startsWith('保存')) return 'replaced';
  return 'soon';
}

/* ══════════════════════ 卡片修正 ══════════════════════ */

/**
 * 个别卡片要额外处理的地方。键是「页面|卡片 id」。
 *
 *   hide    不画：这一页上面有真的表单或列表，设置里再画一份会让人在错的地方填
 *   banner  整张卡片的说明（盖过自动算出来的那句）
 *   fold    折叠组里的说明（按折叠组标题）
 *   sub     替换卡片副标题（原型照桌面版写的，有几句对网页版不成立）
 *   notes   替换卡片里的说明行。`was` 是原文，有测试盯着它还在原型里 ——
 *           原型改了措辞，这里的修正会失效，测试会红，逼人回来重看一遍
 */
export interface CardFix {
  hide?: true;
  banner?: { status: 'soon' | 'desktop' | 'elsewhere'; text: Trilingual };
  /**
   * 不挂整卡横幅，只在没接上的那几行上标。给「主要内容是几句真话、只有一项没接上」的卡片用 ——
   * 比如「VIP 到期之后会怎样」：说明都是真的，只有「到期前提醒我」没做，
   * 整卡一条「这一块还没做」会让人以为那几句说明也不算数
   */
  noBanner?: true;
  /**
   * 替换或去掉小标题。`null` = 不画这一行。
   *
   * 用在「小标题下面的东西搬到别的卡片上了」：喊话那张卡的「添加小伙伴」
   * 现在在上面那张名单卡里，留着这个标题会让人在错的地方找添加按钮。
   * 和 notes 一样，`was` 必须还在原型里（有测试盯着）。
   */
  sects?: Readonly<Record<string, Trilingual | null>>;
  fold?: Readonly<Record<string, Trilingual>>;
  sub?: Trilingual | null;
  notes?: ReadonlyArray<{ was: string; now: Trilingual | null }>;
}

export const CARD_FIXES: Readonly<Record<string, CardFix>> = {
  /* ── 上面有真表单 / 真列表的 ── */
  'wallet|监控钱包': { hide: true },
  'tokenwatch|添加要监控的代币': { hide: true },
  'tokenwatch|监控中的代币': { hide: true },
  'reminders|新增事项': { hide: true },
  'reminders|事项列表': { hide: true },
  /* 备忘录两张卡同理：这一页上面有真的表单和真的列表（见 pages/MemoPage.tsx） */
  'memo|新增备忘': { hide: true },
  'memo|备忘列表': { hide: true },
  /* 「网络与出口」整张卡：三项和出口检测网页版都用不上（服务器不代发 GMGN 请求），不画 */
  'wallet|网络与出口': { hide: true },
  /* 「API Key 池（轮换使用）」：多 Key 轮换是桌面版的；网页版的 Key 和钥匙都在扩展设置页里（老板 2026-10-30：别让用户看到） */
  'wallet|API Key 池（轮换使用）': { hide: true },
  'wallet|额度消耗预估': {
    banner: {
      status: 'elsewhere',
      text: T('在扩展图标的弹窗里看：钱包偏多、额度不够时会写「只够盯几个」，以及是额度还是排班不够。',
        '在擴充功能圖示的彈窗裏看：錢包偏多、額度不夠時會寫「只夠盯幾個」，以及是額度還是排班不夠。',
        'See it in the extension popup: it says how many wallets your quota covers and which limit is the bottleneck.')
    }
  },
  /* 声音与语音：网页版用浏览器自带的声音念（lib/voice.ts），原型那句「服务器合成 Edge 音色」不对了 */
  'general|声音与语音': { sub: T('提醒用 Edge 神经语音念（音质最好）：用 Edge 浏览器的直接用浏览器自带的那一套；别的浏览器由我们的服务器合成，念过的句子存在你电脑上，下次不用再下载。都不通才退回浏览器自带的普通声音。网页开着时网页念，扩展在线时扩展念。',
    '提醒用 Edge 神經語音念（音質最好）：用 Edge 瀏覽器的直接用瀏覽器內建的那一套；別的瀏覽器由我們的伺服器合成，念過的句子存在你電腦上，下次不用再下載。都不通才退回瀏覽器內建的普通聲音。網頁開著時網頁念，擴充功能在線時擴充功能念。',
    'Alerts are read with Edge neural voices (best quality): in the Edge browser its built-in copy is used directly; elsewhere our server synthesizes them and lines you have heard are kept on your computer so they are not downloaded again. Only if all of that fails does it fall back to the plain browser voice. The site speaks while open; the extension speaks while online.') },
  /* 我的邀请：六张卡都由 pages/ReferralPage.tsx 的真界面代替 */
  'referral|我的邀请链接': { hide: true },
  'referral|战绩': { hide: true },
  'referral|越邀越多，比例越高': { hide: true },
  'referral|返佣怎么拿': { hide: true },
  'referral|明细': { hide: true },
  'referral|规则说明': { hide: true },
  /* 交易纪律第 13 条「手动补录一次抵抗」：补录表单画在第 14 条那张卡上面（pages/Fomo.tsx 的 FomoLogCard）——
     记完马上在下面的列表里看到，不用在两张卡之间来回找 */
  'rules|rules:13': { hide: true },

  /* ── 对网页版不成立的话 ── */
  /* 「到点没处理时的反复催办」那句「并取消未发出的 Bark 连发」现在是真的了
     （Bark 要响得比 30 秒久时每 30 秒再推一条，处理 / 推迟 / 改 / 删都会把排着的那几条取消，
     见 @jyfk/scheduler 的 cancelRingFollowUps），所以原来替换那句话的修正拿掉了 */
  'reminders|通知通道与时机矩阵': {
    sub: T('每个通道在提前、到点、催办三个时机里发不发。页面里的弹窗和语音不受这里管。', '每個通道在提前、到點、催辦三個時機裏發不發。頁面裏的彈窗和語音不受這裏管。', 'Whether each channel is used for lead, due and nag alerts. In-page popups and voice are not affected.')
  },
  /* Bark 卡片的四个折叠组 2026-10-01 接上了（到点 / 催办两档、通用开关、加密推送），
     原来挂在这里的「还没接上」横幅一起拿掉。只剩「提前提醒分级规则」那个定制块还没做 */
  'general|链热度监控': {
    notes: [{ was: '总览页的链热度卡被关掉时，从这里找回设置入口。', now: T('链热度的设置（盯哪几条链、多热算热、发到哪里）都在「链热度」那一页。', '鏈熱度的設定（盯哪幾條鏈、多熱算熱、發到哪裏）都在「鏈熱度」那一頁。', 'Chain-heat settings (which chains, how hot counts, where alerts go) live on the Chain heat page.') }]
  },
  /* 这张卡做完了：名单和添加表单由 BuddyManageCard 画成上面一张「喊话小伙伴名单」，
     剩下的设置项（级别、音量、铃声、响铃秒数、默认文案、每日上限）留在这张卡上。
     所以副标题改成说清这张管什么，原型那条说明也不再重复一遍 */
  'general|一键喊话小伙伴（Bark）': {
    /* 「添加小伙伴」搬到上面那张名单卡里了：留着这个小标题会让人在这里找添加按钮 */
    sects: { '添加小伙伴': null },
    sub: T(
      '上面那张卡管名单，这里管喊起来什么样：级别、铃声、响多久、默认说什么、每人每天最多几次。',
      '上面那張卡管名單，這裏管喊起來什麼樣：級別、鈴聲、響多久、預設說什麼、每人每天最多幾次。',
      'The card above manages the list; these control how a call sounds: level, ring time, default text and the daily cap.'
    ),
    notes: [{
      was: '网页版新增：添加后先给对方发一条确认推送，对方点同意才生效；每条喊话都带"不再接收"链接；同一个小伙伴每天最多被喊 N 次。',
      now: null
    }]
  },
  'general|数据与维护': {
    sub: T('桌面版存本地 JSON；网页版存在服务器的数据库里。', '桌面版存本機 JSON；網頁版存在伺服器的資料庫裏。', 'The desktop app kept local JSON; the web version stores everything in our database.')
  },
  /* 纪律 5 极度耐心：桌面版是本机角落里的一个 toast，网页版**真的会推到你手机上**，
     而且有一道每天 36 条的上限。这两件事都得在卡片上说出来 ——
     不说的话，用户把间隔设成 1 分钟，第 37 条起就悄悄不发了 */
  'rules|rules:5': {
    sub: T(
      '每隔一段时间念一句你自己写的话，推到你配好的通道（安静送达，不响铃）。一天有条数上限（出厂 36 条），免得把当天的提醒额度都用在这上面。',
      '每隔一段時間念一句你自己寫的話，推到你配好的通道（安靜送達，不響鈴）。一天有條數上限（出廠 36 條），免得把當天的提醒額度都用在這上面。',
      'Every so often it sends one of your own lines to your channels (quietly, no ring). There is a daily cap (36 by default) so it does not use up your daily alert budget.'
    )
  },
  /* 纪律 6 每日核心心法：交付方式和桌面版不一样（横幅 + 推送，不是全屏弹窗），
     而且「跳过」不记破戒 —— 这两条都要写明白 */
  'rules|rules:6': {
    sub: T(
      '到点在总览页顶上钉一条，并推送到你配好的通道。过了一遍点一下就好；要求抄写时抄对才算过。跳过不记破戒，但会记一笔。',
      '到點在總覽頁頂上釘一條，並推送到你配好的通道。過了一遍按一下就好；要求抄寫時抄對才算過。跳過不記破戒，但會記一筆。',
      'At the set time it pins a banner to the top of the overview and pushes to your channels. Press once to clear it, or type the line out if you asked for that. Skipping is not a violation, but it is logged.'
    )
  },
  /* 原型写「26 项统计…Profit Factor…」—— 网页版总览那张卡算的没有那么多项，也没有 Profit Factor */
  'rules|rules:9': {
    sub: T(
      '胜率、盈亏比、期望值 R、SQN、最大回撤、最长连亏……',
      '勝率、盈虧比、期望值 R、SQN、最大回撤、最長連虧……',
      'Win rate, payoff ratio, expectancy R, SQN, max drawdown, longest losing streak…'
    )
  },
  'look|账号与安全': {
    notes: [{
      was: '我们永远不会索要你的助记词或钱包私钥。GMGN 只读 Key 加密保存，页面只显示末 4 位。',
      now: T(
        '我们永远不会索要你的助记词或钱包私钥。GMGN 的 Key 只填在浏览器扩展里，留在你的浏览器上，不上传到我们的服务器。',
        '我們永遠不會索要你的助記詞或錢包私鑰。GMGN 的 Key 只填在瀏覽器擴充功能裏，留在你的瀏覽器上，不上傳到我們的伺服器。',
        'We will never ask for your seed phrase or wallet private key. Your GMGN key goes only into the browser extension and is never uploaded.'
      )
    }]
  },
  'billing|买 VIP': {
    sub: T('下单时默认选哪条链、哪个币。', '下單時預設選哪條鏈、哪個幣。', 'Which chain and coin to preselect at checkout.')
  },
  'billing|VIP 到期之后会怎样': {
    noBanner: true,
    notes: [{
      /* 「16 条纪律全部照常」—— 16 条里有几条网页版还没做（交易纪律页上标着），不能说「16 条」 */
      was: '风控一直在跑：16 条纪律、熔断、提醒全部照常，免费版都有。',
      now: T(
        '风控一直在跑：纪律、熔断、提醒和 VIP 时一模一样，免费版都有。',
        '風控一直在跑：紀律、熔斷、提醒和 VIP 時一模一樣，免費版都有。',
        'Risk control keeps running: rules, breakers and reminders work exactly as on VIP.'
      )
    }, {
      was: '超出免费版数量的钱包、代币、链先暂停，设置全都留着，再开 VIP 自动恢复。',
      now: T(
        '超出免费版数量的钱包和代币：到期后不能再新加、也不能再打开超出的部分；已经开着的目前会继续运行（「到期自动暂停、续费自动恢复」还没做）。设置全都留着。',
        '超出免費版數量的錢包和代幣：到期後不能再新加、也不能再打開超出的部分；已經開著的目前會繼續運行（「到期自動暫停、續費自動恢復」還沒做）。設定全都留著。',
        'Wallets and tokens beyond the Free limits: after expiry you cannot add or switch on more; the ones already on keep running for now ("pause on expiry, resume on renewal" is not built). Your settings are kept.'
      )
    }]
  }
};

export function cardFix(page: string, cardId: string): CardFix | null {
  return CARD_FIXES[page + '|' + cardId] ?? null;
}

/** 说明行按修正表换掉。返回 null = 这一句不画 */
export function fixedNote(fix: CardFix | null, text: Trilingual | null): Trilingual | null {
  if (!text) return null;
  const f = fix?.notes?.find(n => n.was === text.zh);
  return f ? f.now : text;
}

/** 小标题按修正表换掉。返回 null = 这一行不画 */
export function fixedSect(fix: CardFix | null, text: Trilingual | null): Trilingual | null {
  if (!text) return null;
  const m = fix?.sects;
  return m && Object.hasOwn(m, text.zh) ? (m[text.zh] ?? null) : text;
}
